import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {startWith} from 'rxjs';

import {FieldConfig, FieldOption} from '../../../models/form-config.model';

/**
 * MultiselectField — selezione multipla a tendina con checkbox.
 *
 * Il valore del FormControl è un array di `value` selezionati.
 * Normalizza le opzioni (string[] | number[] | FieldOption[]) e si sincronizza
 * bidirezionalmente col FormControl dell'engine.
 */
@Component({
  selector: 'app-multiselect-field',
  standalone: true,
  imports: [ReactiveFormsModule],
  template: `
    <div class="ms" [class.ms--open]="isOpen()" [class.ms--error]="isInvalid()">
      <label class="ms__label">
        {{ field.label }}
        @if (field.required) {
          <span class="ms__req" aria-hidden="true"> *</span>
        }
      </label>

      <button
        type="button"
        class="ms__trigger"
        [disabled]="isDisabled()"
        [attr.aria-expanded]="isOpen()"
        (click)="toggle()"
      >
        <span class="ms__value" [class.ms__value--placeholder]="selectedCount() === 0">
          {{ summary() }}
        </span>
        <svg class="ms__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      @if (isOpen()) {
        <ul class="ms__panel" role="listbox" aria-multiselectable="true">
          @for (opt of normalizedOptions(); track opt.value) {
            <li class="ms__option" role="option" [attr.aria-selected]="isSelected(opt.value)">
              <label class="ms__option-label">
                <input
                  type="checkbox"
                  [checked]="isSelected(opt.value)"
                  (change)="toggleOption(opt.value)"
                />
                <span>{{ opt.label }}</span>
              </label>
            </li>
          } @empty {
            <li class="ms__empty">Nessuna opzione disponibile</li>
          }
        </ul>
      }
    </div>
  `,
  styles: [`
    :host { display: block; width: 100%; }
    .ms { position: relative; width: 100%; }
    .ms__label { display: block; margin-bottom: 4px; font-size: .85rem; color: var(--text-secondary, #6b7280); }
    .ms__req { color: #dc2626; }
    .ms__trigger {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 10px 12px;
      background: var(--surface, #fff);
      border: 1px solid var(--border, #d1d5db);
      border-radius: var(--radius, 8px);
      cursor: pointer;
      text-align: left;
      font: inherit;
    }
    .ms__trigger:disabled { opacity: .6; cursor: not-allowed; }
    .ms--error .ms__trigger { border-color: #dc2626; }
    .ms__value { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .ms__value--placeholder { color: var(--text-placeholder, #9ca3af); }
    .ms__chevron { flex: 0 0 auto; transition: transform .15s ease; }
    .ms--open .ms__chevron { transform: rotate(180deg); }
    .ms__panel {
      position: absolute;
      z-index: 30;
      left: 0; right: 0;
      margin: 4px 0 0;
      padding: 4px;
      list-style: none;
      max-height: 240px;
      overflow-y: auto;
      background: var(--surface, #fff);
      border: 1px solid var(--border, #d1d5db);
      border-radius: var(--radius, 8px);
      box-shadow: 0 8px 24px rgba(0,0,0,.12);
    }
    .ms__option-label {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      border-radius: 6px;
      cursor: pointer;
    }
    .ms__option-label:hover { background: var(--surface-hover, #f3f4f6); }
    .ms__empty { padding: 10px; color: var(--text-secondary, #6b7280); }
  `],
})
export class MultiselectFieldComponent implements OnInit {
  @Input({required: true}) field!: FieldConfig;
  @Input({required: true}) control!: FormControl;

  private readonly elRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly isOpen = signal(false);

  /** Trigger reattivo: bumpato a ogni cambio valore/stato del control. */
  private readonly _tick = signal(0);

  readonly normalizedOptions = signal<FieldOption[]>([]);

  ngOnInit(): void {
    this.normalizedOptions.set(this.normalizeOptions());

    this.control.valueChanges
      .pipe(startWith(this.control.value), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this._tick.update(v => v + 1));
  }

  readonly selectedValues = computed<unknown[]>(() => {
    this._tick(); // dipendenza reattiva
    const v = this.control.value;
    return Array.isArray(v) ? v : [];
  });

  readonly selectedCount = computed(() => this.selectedValues().length);

  readonly summary = computed(() => {
    const selected = this.selectedValues();
    if (!selected.length) return this.field.label;
    const labels = this.normalizedOptions()
      .filter(o => selected.some(s => String(s) === String(o.value)))
      .map(o => o.label);
    return labels.length <= 2 ? labels.join(', ') : `${labels.length} selezionate`;
  });

  readonly isDisabled = computed(() => {
    this._tick();
    return this.control.disabled;
  });

  readonly isInvalid = computed(() => {
    this._tick();
    return this.control.invalid && this.control.touched;
  });

  isSelected(value: unknown): boolean {
    return this.selectedValues().some(s => String(s) === String(value));
  }

  toggle(): void {
    if (this.isDisabled()) return;
    this.isOpen.update(v => !v);
  }

  toggleOption(value: unknown): void {
    const current = this.selectedValues();
    const exists = current.some(s => String(s) === String(value));
    const next = exists
      ? current.filter(s => String(s) !== String(value))
      : [...current, value];
    this.control.setValue(next);
    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen() && !this.elRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }

  private normalizeOptions(): FieldOption[] {
    if (!this.field.options?.length) return [];
    const first = this.field.options[0];
    if (typeof first === 'string' || typeof first === 'number') {
      return (this.field.options as (string | number)[]).map(v => ({label: String(v), value: v}));
    }
    return this.field.options as FieldOption[];
  }
}
